// Sprint UI compaction (2026-05-11) — sidebar header compaction.
//
// What this is:
//   The small read-only pill that lives where the old "Agents" label
//   sat. Replaces the post-D-013 vestigial `ServerSelector` (which let
//   the user switch between per-VPS dashboards — a model that ceased to
//   exist when D-013 centralised the control plane on vps1.asoasis.com
//   and turned every other VPS into a runner-only host).
//
// What it shows:
//   `🟢 N runners · X% free`
//   where N is the count of runners the control plane knows about and
//   X is the WORST-case 5h cap residual across online runners — i.e.
//   the actual headroom you have if you spawn the next agent against
//   the bottleneck runner. The aggregate is conservative because the
//   scheduler does not let the founder pick which runner an agent
//   lands on; the founder cares about "how full is the system" not
//   "what's my best runner".
//
// Visual states (semantic, not vibes):
//   - green   → every runner online AND worst cap > 20% free.
//   - yellow  → mixed state: one or more offline / draining /
//                incompatible, or worst cap ≤ 20%.
//   - red     → zero runners online (cannot spawn anything).
//
// Click contract:
//   Opens the existing UsageModal (`useAppStore.setUsageModalOpen`).
//   The modal already renders the full RunnersTable that this pill
//   summarises — so the pill is summary + entry point, NOT a parallel
//   surface to maintain. Same UX pattern as Cursor's status indicators
//   (top-bar errors collapse to a count; click expands to the panel).
//
// Why a pill, not a dropdown:
//   The old `ServerSelector` was a switcher; this pill is read-only
//   information. Post-D-013 there is nothing for the user to switch
//   between — the scheduler decides routing automatically. A dropdown
//   would re-import the same trap (an interactive control that can
//   visibly do "nothing useful", inviting clicks that produce no
//   change). A pill makes the affordance honest: glanceable status,
//   one click to drill down.
//
// Hide-when-empty contract:
//   Returns null if the runners store is still pre-hydration AND has
//   zero entries. As soon as the first `/api/runners` poll lands, the
//   pill renders — typically <100 ms after the page mounts because
//   `startRunnersPolling()` fires the first hydrate synchronously
//   on mount (see `runners.store.ts::startRunnersPolling`).
//
// DS.5 (sprint aos-ds-shell, spec §2 D3): the status dot is the DS `Badge`
// (dot, standalone — approved-shell.png's "● 1 runner" line) and the label
// takes the mockup's 11 px semibold role. The tooltip BODY stays on the Radix
// primitive as a named residue: the DS `Tooltip` takes `content: string` at
// v0.2.0, and this body is a component tree (per-runner rows with their own
// state words, testids and stale-age captions the tests read) — DS.8, which
// retires `ui/tooltip`, owns the upstream ask (a rich-content slot).

import { useAppStore } from '../stores/app.store';
import { useRunnersStore } from '../stores/runners.store';
import { useRunnersUsageStore } from '../stores/runners-usage.store';
import { useTranslation } from '../i18n';
import DsBadge from '@ds/data-display/Badge';
import { Badge, type BadgeColor } from './atoms/Badge';
// DS.8 (D9) — `ui/tooltip.tsx` retires. DS.5 handed this file over by name
// (header above): `@ds/feedback/Tooltip` takes `content: string` at v0.2.0
// and this body is a component tree with its own testids and state words, so
// `ds/DsTooltip.tsx` cannot express it. Disposition is D8/D20's shape —
// retire the shadcn WRAPPER (29 LOC of `cn()` over the primitive), keep the
// primitive, product-block the surface. `TOOLTIP_CONTENT_CLASSES` below is
// `ui/tooltip.tsx`'s class block verbatim, so the rendered box is byte-for-byte
// what `dashboard-sidebar` / `dashboard-full` already baseline.
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { deriveRunnerLabel } from './RunnerChip';
import { classifyRunner } from '../lib/runner-availability';
import {
  UsageAgeCaption,
  isUsageReadingStale,
  isFleetAgeCaptionSuppressed,
  oldestUsageAtIso,
  usageAgeMs,
} from './atoms/UsageAgeCaption';
import type { RunnerSnapshot, RunnerUnavailableReason, RunnerUsageEntry } from '../lib/api';

interface PillState {
  color: BadgeColor;
  labelKey: 'allHealthy' | 'singleRunner' | 'degraded' | 'allOffline';
  total: number;
  online: number;
  capWorst: number | null;
}

// Threshold below which the worst cap residual flips the pill to
// yellow. 20% means "you have ≤ 200 messages of headroom on the
// tightest runner before its 5h window saturates" — a useful warning
// signal without being noisy at 40-50% which is fully normal.
const CAP_WARNING_THRESHOLD_PCT = 20;

// `ui/tooltip.tsx`'s `TooltipContent` class block, verbatim (D9). Kept as one
// constant instead of re-inlined at each call site: this file is its only
// consumer today and a second one would be the trigger to extract a product
// block, not to copy the string again (P6).
const TOOLTIP_CONTENT_CLASSES =
  'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

export function RunnersHealthPill() {
  const { t } = useTranslation();
  const runnersById = useRunnersStore((s) => s.runnersById);
  const loaded = useRunnersStore((s) => s.loaded);
  const usageById = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const setUsageOpen = useAppStore((s) => s.setUsageModalOpen);

  const runners = Object.values(runnersById);

  // Pre-hydration AND no cached entries → render nothing. The
  // header's flex layout collapses naturally and the founder doesn't
  // see a "loading…" placeholder that flashes for 50 ms.
  if (!loaded && runners.length === 0) {
    return null;
  }

  const state = derivePillState(runners, usageById);
  const label = renderLabel(t, state);

  // Self-contained `TooltipProvider`. The Sidebar tree does not mount a
  // shared provider (DashboardLayout has none either; `ChatHeader`
  // mounts its own for `RunnerChip`). Without this wrapper Radix
  // throws "Tooltip must be wrapped in a TooltipProvider" and the
  // whole sidebar render fails — caught during the visual-parity
  // gate, not by unit tests (those wrap explicitly).
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setUsageOpen(true)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-foreground hover:bg-accent/40 active:bg-accent/60 transition-colors min-w-0 max-w-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            style={{ minHeight: 'auto', minWidth: 'auto' }}
            aria-label={t('runnersHealth.ariaLabel')}
            data-testid="runners-health-pill"
            data-state={state.color}
          >
            <StatusDot color={state.color} />
            <span className="truncate font-550">{label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          sideOffset={4}
          className={`${TOOLTIP_CONTENT_CLASSES} px-3 py-2 max-w-xs`}
          data-testid="runners-health-tooltip"
        >
          <RunnerTooltipBody runners={runners} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// The pill's dot on the DS `Badge` (dot, standalone). The piece's colour set
// is primary/secondary/danger/success — no WARNING (§6): the degraded state
// keeps its amber by re-pointing the danger family's role on this one node
// to the skin's warning role (a skin role, never a raw value; the piece
// reads `var(--tone-danger-bg)` for its fill).
function StatusDot({ color }: { color: BadgeColor }) {
  if (color === 'warning') {
    return (
      <DsBadge
        variant="dot"
        color="danger"
        standalone
        className="shrink-0"
        style={{ ['--tone-danger-bg' as string]: 'var(--tone-warning-bg)' }}
      />
    );
  }
  return (
    <DsBadge variant="dot" color={color === 'error' ? 'danger' : 'success'} standalone className="shrink-0" />
  );
}

// Returns the visual state derived from the current runners list.
// Pure function so it's covered by unit tests directly.
//
// INV-X: capacity is derived from the authoritative usage cache
// (Anthropic OAuth data via S20.8), not from the heuristic
// `cap5hResidualPct` column. When the usage cache has an entry for a
// runner, `100 - session.percentageUsed` replaces the old cap value.
// When the cache row is missing or in error state, the pill treats
// the runner's cap as unknown.
export function derivePillState(
  runners: RunnerSnapshot[],
  usageById: Record<string, RunnerUsageEntry> = {},
  // Test seam for the staleness cut-off below. Defaults to wall clock.
  now: number = Date.now(),
): PillState {
  const total = runners.length;

  // Sprint runner-quarantine-truth (D013-EC-126) — availability comes
  // from the ONE shared predicate; this used to be an inline duplicate
  // that could (and did) drift from the banner's.
  const online = runners.filter((r) => classifyRunner(r).state === 'available');

  // Derive worst cap from the authoritative usage cache. For each
  // online runner, read `usage.five_hour.utilization` from the cache.
  // Cap residual = 100 - utilization. Missing/error rows → unknown.
  //
  // Sprint usage-last-known-good (D013-EC-172) D3 — rows past the UI
  // staleness threshold are excluded here rather than muted. The pill
  // label is a single line with no room for an age caption beside it, so a
  // carried-forward number would render as a bare "82 % libre" with
  // nothing to qualify it. Dropping it degrades the label to
  // "capacity unknown", which is the honest answer; the tooltip below
  // still shows the number WITH its age for anyone who opens it.
  const capValues = online
    .map((r) => {
      const entry = usageById[r.id];
      if (!entry?.usage?.five_hour) return null;
      if (isUsageReadingStale(entry.usageAtIso, now)) return null;
      return 100 - entry.usage.five_hour.utilization;
    })
    .filter((v): v is number => typeof v === 'number');
  const capWorst = capValues.length > 0 ? Math.min(...capValues) : null;

  if (total === 0 || online.length === 0) {
    return { color: 'error', labelKey: 'allOffline', total, online: 0, capWorst: null };
  }

  const someDegraded = online.length < total;
  const capLow = capWorst !== null && capWorst <= CAP_WARNING_THRESHOLD_PCT;

  if (someDegraded || capLow) {
    return {
      color: 'warning',
      labelKey: someDegraded ? 'degraded' : total === 1 ? 'singleRunner' : 'allHealthy',
      total,
      online: online.length,
      capWorst,
    };
  }

  return {
    color: 'success',
    labelKey: total === 1 ? 'singleRunner' : 'allHealthy',
    total,
    online: online.length,
    capWorst,
  };
}

type TranslateFn = ReturnType<typeof useTranslation>['t'];

function renderLabel(t: TranslateFn, state: PillState): string {
  const capSegment =
    state.capWorst === null
      ? null
      : t('runnersHealth.cap.percent', { value: Math.round(state.capWorst) });

  switch (state.labelKey) {
    case 'allOffline':
      return t('runnersHealth.allOffline');
    case 'singleRunner':
      return capSegment
        ? t('runnersHealth.singleRunnerWithCap', { cap: capSegment })
        : t('runnersHealth.singleRunner');
    case 'degraded':
      return t('runnersHealth.degraded', {
        online: state.online,
        total: state.total,
      });
    case 'allHealthy':
      return capSegment
        ? t('runnersHealth.allHealthyWithCap', {
            count: state.total,
            cap: capSegment,
          })
        : t('runnersHealth.allHealthy', { count: state.total });
  }
}

// Sprint runner-quarantine-truth (D3) — founder-mode label key per
// quarantine reason. Exported for the pill's per-state-label tests.
// Never render the enum value itself (founder-first §5); the developer
// detail stays in Settings → Servers.
export const QUARANTINE_REASON_LABEL_KEY: Record<
  RunnerUnavailableReason,
  Parameters<TranslateFn>[0]
> = {
  missing_credential: 'runnersHealth.reason.missingCredential',
  claude_account_blocked: 'runnersHealth.reason.claudeAccountBlocked',
  claude_not_authenticated: 'runnersHealth.reason.claudeNotAuthenticated',
  disk_full: 'runnersHealth.reason.diskFull',
  wrong_workspace: 'runnersHealth.reason.wrongWorkspace',
  incompatible_version: 'runnersHealth.reason.incompatibleVersion',
};

// Exported for tests — jsdom cannot reliably open the Radix tooltip,
// so the per-runner state-word matrix is asserted on the body directly.
export function RunnerTooltipBody({ runners }: { runners: RunnerSnapshot[] }) {
  const { t } = useTranslation();
  const usageById = useRunnersUsageStore((s) => s.entriesByRunnerId);
  // Sprint usage-single-emitter (D013-EC-166) D3 — this surface is a pure
  // reader (verified: it has never had a `useEffect` or a fetch), and
  // after this sprint the poll that feeds it is cache-only. The age line
  // is what keeps "X % libre" honest about when it was measured.
  //
  // Sprint usage-last-known-good (D013-EC-172) D3 — sourced from the rows
  // on screen (oldest wins) instead of the store's fleet-level
  // `fetchedAtIso`, which is a different clock entirely after D1.
  const tooltipEntries = runners
    .map((r) => usageById[r.id])
    .filter((e): e is RunnerUsageEntry => e !== undefined);
  const atIso = oldestUsageAtIso(tooltipEntries);

  if (runners.length === 0) {
    return <span>{t('runnersHealth.tooltip.empty')}</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-caption font-550 opacity-80">
        {t('runnersHealth.tooltip.title')}
      </div>
      <div className="flex flex-col gap-1">
        {runners.map((r) => (
          <RunnerTooltipRow key={r.id} runner={r} usageEntry={usageById[r.id]} />
        ))}
      </div>
      {!isFleetAgeCaptionSuppressed(tooltipEntries) && (
        <UsageAgeCaption
          atIso={atIso}
          className="text-caption opacity-70"
          data-testid="runners-health-tooltip-age"
        />
      )}
    </div>
  );
}

function RunnerTooltipRow({
  runner,
  usageEntry,
}: {
  runner: RunnerSnapshot;
  usageEntry?: RunnerUsageEntry;
}) {
  const { t } = useTranslation();
  const label = deriveRunnerLabel(runner.id);
  // Sprint runner-quarantine-truth (D3, D013-EC-126) — state from the
  // ONE shared predicate; the flat "unavailable" gave the founder zero
  // signal during the fleet-quarantine incident (12 identical rows, no
  // reason). Offline keeps the error dot; draining / quarantined are
  // warning-class (degraded, not gone) — same tokens the banner uses.
  const availability = classifyRunner(runner);
  const dotColor: BadgeColor =
    availability.state === 'available'
      ? 'success'
      : availability.state === 'offline'
        ? 'error'
        : 'warning';

  const tail = (() => {
    switch (availability.state) {
      case 'offline':
        return t('runnersHealth.tooltip.stateOffline');
      case 'draining':
        return t('runnersHealth.tooltip.stateDraining');
      case 'quarantined':
        return t(QUARANTINE_REASON_LABEL_KEY[availability.reason]);
      case 'available':
        // INV-X: derive cap from authoritative usage cache
        if (!usageEntry?.usage?.five_hour) {
          return t('runnersHealth.tooltip.capUnknown');
        }
        return t('runnersHealth.tooltip.capFree', {
          value: Math.round(100 - usageEntry.usage.five_hour.utilization),
        });
    }
  })();

  // Sprint usage-last-known-good (D013-EC-172) D3 — past the threshold the
  // number is muted and carries its own age, so a carried-forward reading
  // is never mistaken for a live one on this surface either.
  const stale =
    availability.state === 'available' &&
    usageEntry?.usage?.five_hour != null &&
    isUsageReadingStale(usageEntry.usageAtIso);

  return (
    <div
      className="flex items-center gap-2 text-caption tabular-nums"
      data-testid={`runners-health-tooltip-row-${runner.id}`}
      data-runner-state={availability.state}
      data-usage-stale={stale ? 'true' : 'false'}
    >
      <Badge variant="dot" color={dotColor} className="w-1.5 h-1.5 shrink-0" />
      <span className="font-550 min-w-[3ch]">{label}</span>
      <span className={stale ? 'opacity-40' : 'opacity-70'}>{tail}</span>
      {/* Parseability, not staleness — see the picker's note. */}
      {stale && usageAgeMs(usageEntry?.usageAtIso) !== null && (
        <UsageAgeCaption
          atIso={usageEntry?.usageAtIso}
          className="text-caption opacity-50"
          data-testid={`runners-health-tooltip-row-age-${runner.id}`}
        />
      )}
    </div>
  );
}
